/* ============================================================================
   Phone field — country selector + validation, for every input[type="tel"].

   Three scripts share these fields and each has one job:
     phone-input.js       keeps keystrokes clean (strips junk, caps digits)
     libphonenumber-min.js  the country data and validation rules
     this file            the country selector, validation, and normalising

   ── Why the selector is positioned rather than styled ──────────────────────
   These forms style their inputs very differently: .pl-contact__input is a
   60px pill, .rfq-form__field input a 6px box at 44px tall, .csf__field a 30px
   pill with 28px side padding, .industry-rfq__input a 10px box — eight-plus
   variants in all. Rebuilding a joined control per form, or copying computed
   borders onto a wrapper, would mean either per-form CSS or losing each
   input's own :focus and transition rules.

   So the input is left completely untouched — its border, radius, background,
   focus ring and transitions all still come from its own stylesheet. The
   selector is absolutely positioned inside the input's left padding, and the
   input's padding-left is grown to make room. The result reads as one split
   field on every form, adapts to any form added later, and needs no per-form
   CSS at all.

   ── Country handling ───────────────────────────────────────────────────────
   The list, dial codes and validation rules all come from libphonenumber-js;
   country names come from Intl.DisplayNames and flags are derived
   arithmetically from the ISO code. There is no country table in this theme
   to drift out of date.

   The selector is a real <select>, visually transparent over a compact
   "flag +code" display. That keeps the native picker, keyboard support,
   type-ahead and mobile behaviour for free, while the closed state stays
   narrow — a native select would otherwise show the full country name.
   Options are built on first interaction: 245 countries times eleven fields
   is a lot of DOM to create for a field most visitors never touch.

   ── Values ─────────────────────────────────────────────────────────────────
   On blur a valid number is rewritten in international format, so what
   reaches Shopify is unambiguous and dialable from the enquiry email —
   picking GB and typing "020 7946 0958" submits "+44 20 7946 0958".
   Rewriting happens only on blur, never mid-typing: doing it per keystroke
   moves the caret and breaks editing and pasting, which is the same reason
   phone-input.js avoids reformatting.

   setCustomValidity() plugs into the Constraint Validation API every form
   already relies on (checkValidity() / reportValidity() / a plain submit), so
   no per-form JS needed changing for any of this.
   ========================================================================== */
(() => {
  const DEFAULT_COUNTRY = 'US';
  const MOUNTED = 'data-phone-field';

  /** input -> { wrap, select, display, country } */
  const fields = new WeakMap();
  let countryCache = null;

  const lib = () => window.libphonenumber;

  const regionNames =
    typeof Intl !== 'undefined' && Intl.DisplayNames
      ? new Intl.DisplayNames(undefined, { type: 'region' })
      : null;

  /** ISO 3166-1 alpha-2 -> flag emoji, via regional indicator code points. */
  function flagFor(iso) {
    if (!iso || iso.length !== 2) return '';
    return iso.toUpperCase().replace(/./g, (ch) => String.fromCodePoint(127397 + ch.charCodeAt(0)));
  }

  /** "GB" -> "United Kingdom", falling back to the raw code. */
  function nameFor(iso) {
    if (!iso) return iso || '';
    try {
      return (regionNames && regionNames.of(iso)) || iso;
    } catch (e) {
      return iso;
    }
  }

  function callingCodeFor(iso) {
    try {
      return lib().getCountryCallingCode(iso) || '';
    } catch (e) {
      return '';
    }
  }

  /** Every country the library knows, named and sorted. Built once. */
  function countries() {
    if (countryCache) return countryCache;
    let list = [];
    try {
      list = lib()
        .getCountries()
        .map((iso) => ({ iso, name: nameFor(iso), code: callingCodeFor(iso) }))
        .filter((c) => c.code);
      list.sort((a, b) => a.name.localeCompare(b.name));
    } catch (e) {
      list = [];
    }
    countryCache = list;
    return list;
  }

  /* ------------------------------------------------------------- detection */

  /**
   * What the library makes of the current value.
   *
   * `country` only resolves once the number is unambiguous — +1 is shared by
   * the US, Canada and some twenty Caribbean countries, +44 by GB/GG/IM/JE —
   * whereas `callingCode` resolves as soon as the prefix is recognised. The
   * difference is what separates "no such country code" from "country code is
   * fine, number is incomplete".
   */
  function detect(value, fallback) {
    if (!lib() || !lib().AsYouType) return { country: '', callingCode: '' };
    try {
      // Parenthesised: `new lib().AsYouType()` would parse as
      // `(new lib()).AsYouType()` and throw, and the catch below would hide it.
      const AsYouType = lib().AsYouType;
      const typed = new AsYouType(value.charAt(0) === '+' ? undefined : fallback);
      typed.input(value);
      return {
        country: typed.country || '',
        callingCode: (typed.getCallingCode && typed.getCallingCode()) || '',
      };
    } catch (e) {
      return { country: '', callingCode: '' };
    }
  }

  /**
   * Why a number failed, in the visitor's terms. validatePhoneNumberLength
   * separates "too short" and "too long" from "right length but not a real
   * number" — the difference between a half-typed number and 000-000-0000.
   */
  function messageFor(value, country, fallback) {
    const international = value.charAt(0) === '+';
    const where = nameFor(country || (international ? '' : fallback));
    let problem = '';
    try {
      problem =
        (international ? lib().validatePhoneNumberLength(value) : lib().validatePhoneNumberLength(value, fallback)) ||
        '';
    } catch (e) {
      problem = '';
    }

    if (problem === 'INVALID_COUNTRY') {
      return international
        ? 'Check the country code — that is not one we recognise.'
        : 'Enter a valid phone number, or pick the country it belongs to.';
    }
    if (problem === 'TOO_SHORT' || problem === 'INVALID_LENGTH') {
      return where ? `That ${where} number looks incomplete.` : 'That number looks incomplete.';
    }
    if (problem === 'TOO_LONG') {
      return where ? `That ${where} number has too many digits.` : 'That number has too many digits.';
    }
    if (where) return `That does not look like a valid ${where} phone number.`;
    return 'That number is not recognised — check the country and the digits.';
  }

  /* ----------------------------------------------------------------- the UI */

  function setCountry(el, iso) {
    const f = fields.get(el);
    if (!f || !iso) return;
    f.country = iso;
    f.display.textContent = `${flagFor(iso)} +${callingCodeFor(iso)}`;
    if (f.select.value !== iso) f.select.value = iso;
    f.select.setAttribute('aria-label', `Country code: ${nameFor(iso)} (+${callingCodeFor(iso)})`);
    // The display width drives the input's padding, so re-measure after it
    // changes: "+1" and "+971" are not the same width.
    measure(el);
  }

  /** Grow the input's left padding so typed digits clear the selector. */
  function measure(el) {
    const f = fields.get(el);
    if (!f) return;
    const width = f.button.offsetWidth;
    if (!width) return;
    const base = f.basePaddingLeft;
    el.style.paddingLeft = `${base + width + 4}px`;
    f.button.style.left = `${Math.max(base - 4, 4)}px`;
  }

  /** Options are only worth building once someone actually opens the list. */
  function fillOptions(el) {
    const f = fields.get(el);
    if (!f || f.filled) return;
    const list = countries();
    if (!list.length) return;
    const frag = document.createDocumentFragment();
    list.forEach((c) => {
      const opt = document.createElement('option');
      opt.value = c.iso;
      opt.textContent = `${flagFor(c.iso)} ${c.name} +${c.code}`;
      frag.appendChild(opt);
    });
    f.select.appendChild(frag);
    f.select.value = f.country;
    f.filled = true;
  }

  function mount(el) {
    if (el.hasAttribute(MOUNTED) || !lib()) return;
    el.setAttribute(MOUNTED, '');

    const style = window.getComputedStyle(el);
    const basePaddingLeft = parseFloat(style.paddingLeft) || 12;

    const wrap = document.createElement('div');
    wrap.className = 'phone-field';

    const button = document.createElement('span');
    button.className = 'phone-field__country';

    const display = document.createElement('span');
    display.className = 'phone-field__code';
    display.setAttribute('aria-hidden', 'true');

    // A real <select>, transparent on top of the display: native picker,
    // keyboard and type-ahead for free, without the closed state widening to
    // fit a full country name. No `name`, so it is never submitted — the
    // number input carries the whole value.
    const select = document.createElement('select');
    select.className = 'phone-field__select';

    const initial = document.createElement('option');
    initial.value = DEFAULT_COUNTRY;
    initial.textContent = nameFor(DEFAULT_COUNTRY);
    select.appendChild(initial);

    button.appendChild(display);
    button.appendChild(select);

    el.parentNode.insertBefore(wrap, el);
    wrap.appendChild(button);
    wrap.appendChild(el);

    fields.set(el, { wrap, button, select, display, country: DEFAULT_COUNTRY, basePaddingLeft, filled: false });
    setCountry(el, DEFAULT_COUNTRY);

    // A field mounted while hidden (the request-a-quote modal, a closed
    // drawer) measures 0px wide, so measure() skips it and the flag and dial
    // code end up drawn over the placeholder once it is shown. Re-measure
    // whenever the selector gets a real size. measure() only changes the
    // input's padding, never the selector's own size, so this cannot loop.
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(() => measure(el)).observe(button);
    }

    // Fill on first interaction, whichever comes first.
    ['pointerdown', 'focus', 'keydown'].forEach((evt) =>
      select.addEventListener(evt, () => fillOptions(el), { once: true })
    );

    select.addEventListener('change', () => {
      setCountry(el, select.value);
      // Re-validate against the newly chosen country, and re-normalise, so
      // switching country fixes a number that was only wrong for the old one.
      validate(el);
      normalise(el);
    });
  }

  /* --------------------------------------------------------- normalise/check */

  /** Rewrite a valid number in international format. Blur-time only. */
  function normalise(el) {
    const f = fields.get(el);
    const value = el.value.trim();
    if (!f || !value || !lib()) return;
    try {
      const parsed = lib().parsePhoneNumberFromString(value, f.country);
      if (parsed && parsed.isValid()) {
        const formatted = parsed.formatInternational();
        if (formatted && formatted !== el.value) el.value = formatted;
      }
    } catch (e) {
      /* leave the value alone if it cannot be parsed */
    }
  }

  /** @param {HTMLInputElement} el */
  function validate(el) {
    const f = fields.get(el);
    const fallback = (f && f.country) || DEFAULT_COUNTRY;

    // An untouched, server-rendered value (an existing address's stored phone
    // number) is not this session's problem. Without this, a number saved
    // before this validation existed would block saving an unrelated edit to
    // the same address, such as a changed city.
    if (el.value === el.defaultValue) {
      el.setCustomValidity('');
      return;
    }

    const value = el.value.trim();

    // Empty is for `required` to judge, not this.
    if (!value) {
      el.setCustomValidity('');
      return;
    }

    // If the library failed to load, do not block submission on that account.
    if (!lib()) {
      el.setCustomValidity('');
      return;
    }

    let valid = false;
    try {
      valid = value.charAt(0) === '+' ? lib().isValidPhoneNumber(value) : lib().isValidPhoneNumber(value, fallback);
    } catch (e) {
      valid = false;
    }

    const { country } = detect(value, fallback);

    // A pasted or typed +44 number should move the selector to match, so the
    // field never shows one country while holding another's number.
    if (country && f && country !== f.country) setCountry(el, country);

    el.setCustomValidity(valid ? '' : messageFor(value, country, fallback));
  }

  /* ------------------------------------------------------------- delegation */

  function isTel(el) {
    return el && el.tagName === 'INPUT' && el.type === 'tel';
  }

  /**
   * Delegated, like phone-input.js, because several of these fields live in
   * the cart drawer and RFQ modal and are injected after first paint. Mounting
   * on focus covers those without watching the whole document for mutations.
   */
  document.addEventListener(
    'focusin',
    (evt) => {
      if (!isTel(evt.target)) return;
      const el = evt.target;
      ensureLib(() => mount(el));
    },
    true
  );

  document.addEventListener('input', (evt) => {
    if (!isTel(evt.target)) return;
    if (!evt.target.validationMessage) return;
    validate(evt.target);
  });

  // focusout bubbles (blur does not), so this still works delegated.
  document.addEventListener('focusout', (evt) => {
    if (!isTel(evt.target)) return;
    validate(evt.target);
    normalise(evt.target);
  });

  // Capture phase, so this runs before a form's own submit handler reads
  // checkValidity() — those handlers are bubble-phase listeners on the form
  // itself, which fire after a capturing document-level listener.
  document.addEventListener(
    'submit',
    (evt) => {
      const form = evt.target;
      if (!(form instanceof HTMLFormElement)) return;
      form.querySelectorAll('input[type="tel"]').forEach((el) => {
        validate(el);
        normalise(el);
      });
    },
    true
  );

  /* ---------------------------------------------------------- library load */

  /**
   * libphonenumber is ~43KB gzipped and only ten of this theme's sections have
   * a tel field, so it is fetched on demand rather than on every page load:
   * once at DOMContentLoaded if the page has a phone field, otherwise on first
   * focus of one injected later (cart drawer, RFQ modal). The URL comes from
   * Liquid via data-phone-lib so no asset path is hardcoded here.
   *
   * Everything that reads the library already guards on lib() and fails open,
   * so a field focused before the fetch lands simply is not blocked.
   */
  let libState = 'idle';
  const waiting = [];

  function ensureLib(done) {
    if (lib()) {
      done();
      return;
    }
    if (done) waiting.push(done);
    if (libState !== 'idle') return;

    const tag = document.querySelector('script[data-phone-lib]');
    const src = tag && tag.getAttribute('data-phone-lib');
    if (!src) {
      libState = 'failed';
      return;
    }

    libState = 'loading';
    const script = document.createElement('script');
    script.src = src;
    script.addEventListener('load', () => {
      libState = 'ready';
      while (waiting.length) waiting.shift()();
    });
    script.addEventListener('error', () => {
      libState = 'failed';
      waiting.length = 0;
    });
    document.head.appendChild(script);
  }

  // Mount anything already on the page, so the selector is visible before the
  // field is touched. Deferred scripts run before DOMContentLoaded fires.
  function mountAll() {
    if (!document.querySelector('input[type="tel"]')) return;
    ensureLib(() => document.querySelectorAll('input[type="tel"]').forEach(mount));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountAll);
  else mountAll();

  // Padding depends on measured widths, which are wrong until fonts land.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => document.querySelectorAll('input[type="tel"]').forEach(measure));
  }
  window.addEventListener('resize', () => document.querySelectorAll('input[type="tel"]').forEach(measure));
})();
